import React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Bot,
  ChevronRight,
} from "lucide-react";

function ErrorList({ errors }) {
  const getSeverityBadge = (severity) => {
    const colors = {
      critical: "bg-red-500/20 text-red-400 border-red-500/30",
      error: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      warning: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    };
    return colors[severity] || colors.error;
  };

  if (!errors || errors.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-gray-600" />
        </div>
        <p className="text-gray-400 font-medium">No errors captured yet</p>
        <p className="text-sm text-gray-500 mt-1">
          Errors will appear here once your app sends them
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errors.map((error) => (
        <Link
          key={error.id}
          to={`/errors/${error.id}`}
          className="block group bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800/50 hover:border-cyan-400/20 rounded-xl p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-cyan-500/5"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span
                  className={`px-2.5 py-0.5 text-xs font-medium rounded-lg border ${getSeverityBadge(error.severity)}`}
                >
                  {error.severity}
                </span>
                {error.has_ai_analysis && (
                  <span className="flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-lg bg-gradient-to-r from-cyan-500/20 to-blue-600/20 text-cyan-400 border border-cyan-400/20">
                    <Bot className="w-3 h-3" />
                    AI Analyzed
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {new Date(error.created_at).toLocaleString()}
                </span>
              </div>

              <div className="font-medium text-white truncate group-hover:text-cyan-400 transition-colors duration-200">
                {error.type}: {error.message}
              </div>

              {error.metadata && Object.keys(error.metadata).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(error.metadata)
                    .slice(0, 3)
                    .map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-block text-xs bg-gray-800/50 px-2 py-0.5 rounded-lg text-gray-400 border border-gray-700/50"
                      >
                        {key}:{" "}
                        {typeof value === "object"
                          ? JSON.stringify(value).slice(0, 20)
                          : String(value).slice(0, 20)}
                      </span>
                    ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 ml-4">
              {error.status === "fixed" ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Clock className="w-5 h-5 text-yellow-500" />
              )}
              <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-cyan-400 transition-colors duration-200" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default ErrorList;
